import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { useWallet } from '../../hooks/useWallet'
import { findBridgeKeyForNetworkId } from '../../services/helperService/findBridgeKeyForNetworkId'
import { selectConfig, selectRequiredChain } from '../../store/selectors/portal.selectors'
import { selectConnectedNetwork } from '../../store/selectors/user.selectors'
import type { RequireChainContentProps } from './types/RequireChainTypes'
import { RequireChainModal } from './components/RequireChainModal'
import { RequireChainPopup } from './components/RequireChainPopup'
import { useIsMobile } from '../../hooks/useIsMobile'
import { clearRequiredChain } from '../../store/actions/user.actions'

export const RequireChain = () => {
  const dispatch = useAppDispatch()

  const requiredChain = useAppSelector(selectRequiredChain)
  const connectedNetwork = useAppSelector(selectConnectedNetwork)
  const bridgeConfig = useAppSelector(selectConfig)

  const [mustSwitchNetwork, setMustSwitchNetwork] = useState(false)

  const wallet = useWallet()
  const { isMobile } = useIsMobile()

  const { chainName = '', resolve } = requiredChain || {}
  const requiredNetworkId = useMemo(
    () => (chainName && bridgeConfig ? bridgeConfig?.[chainName]?.networkId || '' : ''),
    [chainName, bridgeConfig],
  )

  useEffect(() => {
    const effect = async () => {
      if (!requiredChain || !chainName || +requiredNetworkId === connectedNetwork) {
        setMustSwitchNetwork(false)
        clearRequiredChain(dispatch)()
        return resolve?.({ done: true })
      }
      try {
        await switchToNetwork()
      } catch (error) {
        setMustSwitchNetwork(true)
      }
    }
    void effect()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only for specific prop change
  }, [requiredChain, resolve, connectedNetwork, requiredNetworkId, chainName])

  const onClickClose = useCallback(() => {
    if (requiredChain) {
      requiredChain.resolve({ done: false, reason: 'Closed network change modal' })
    }
    setMustSwitchNetwork(false)
    clearRequiredChain(dispatch)()
  }, [requiredChain, dispatch])

  const networkName = useMemo(() => {
    if (!requiredChain) {
      return null
    }
    return bridgeConfig?.[requiredChain.chainName]?.name || ''
  }, [requiredChain, bridgeConfig])

  const switchToNetwork = useCallback(async () => {
    const chain = requiredChain?.chainName
    if (chain && bridgeConfig?.[chain]) {
      await wallet?.connectionKit?.switchNetwork(requiredChain?.chainName, bridgeConfig[chain])
    }
  }, [requiredChain?.chainName, wallet, bridgeConfig])

  const connectedNetworkName = connectedNetwork
    ? findBridgeKeyForNetworkId(connectedNetwork.toString(), bridgeConfig || {}) || ''
    : ''

  const props: RequireChainContentProps = {
    connectedNetworkName,
    chainName,
    networkName: networkName ?? '',
    switchToNetwork: () => {
      void switchToNetwork()
    },
    onClickClose,
    mustSwitchNetwork,
  }

  return isMobile ? <RequireChainPopup {...props} /> : <RequireChainModal {...props} />
}
